<template>
  <popup
    :opened="dataToShow"
    @close="changeToShow(false)"
  >
    <message-header :message-header="messageHeader" />

    <div class="sticky top-0 z-10">
      <!-- Save button -->
      <save-button
        :saving="saving"
        @save="saveBeneficiary"
        class="right-15 top-15"
      />
    </div>

    <div class="m-5 lg:m-15 2xl:m-20">
      <h3>
        Modifier le compte bénéficiaire de <br />
        {{ beneficiaryToEdit.firstname }}
        <span class="uppercase">{{ beneficiaryToEdit.lastname }}</span>
      </h3>
      <div class="w-full flex justify-center my-10">
        <ProfilePicture
          :show-edit-link="true"
          :other-object="beneficiaryToEdit"
          size="max-w-36 w-36 h-36"
          @changeProfileImageToAnotherUser="
            (urlPicture) => {
              changeProfileImageToAnotherUser(urlPicture);
            }
          "
        />
      </div>

      <div class="flex justify-center mb-10">
        <div class="border-dashed border-gray-300 border-2 rounded-lg">
          <div class="my-4 mx-8">
            <p class="text-sm italic font-medium text-gray-600">
              Nom d'utilisateur
            </p>
            <h4 class="text-indigo-600">
              {{ beneficiaryToEdit.username }}
            </h4>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-12 gap-x-4 gap-y-5 xl:mx-1/10">
        <input-text
          class="lg:col-span-6"
          name="firstname"
          libelle="Prénom"
          :value="beneficiaryToEdit.firstname"
          @input="
            (newValue) => {
              beneficiaryToEdit.firstname = newValue;
            }
          "
          :error="true"
          :msg-error="((errors || [])['user.firstname'] || [])[0]"
        />
        <input-text
          class="lg:col-span-6"
          name="lastname"
          libelle="Nom"
          :value="beneficiaryToEdit.lastname"
          @input="
            (newValue) => {
              beneficiaryToEdit.lastname = newValue;
            }
          "
          :error="true"
          :msg-error="((errors || [])['user.lastname'] || [])[0]"
        />
        <input-text
          class="lg:col-span-6"
          name="company"
          libelle="Entreprise"
          :value="beneficiaryToEdit.company"
          :disabled="true"
        />
        <input-text
          class="lg:col-span-6"
          name="fonction"
          libelle="Fonction"
          :value="((structure || {}).pivot || {}).function"
          @input="
            (newValue) => {
              ((structure || {}).pivot || {}).function = newValue;
            }
          "
        />
        <input-text
          class="lg:col-span-6"
          name="email"
          type="email"
          libelle="Email"
          :value="beneficiaryToEdit.email"
          @input="
            (newValue) => {
              beneficiaryToEdit.email = newValue;
            }
          "
          :error="true"
          :msg-error="((errors || [])['user.email'] || [])[0]"
        />
        <input-text
          class="lg:col-span-6"
          name="phone_number"
          libelle="Téléphone"
          :value="beneficiaryToEdit.phone_number"
          @input="
            (newValue) => {
              beneficiaryToEdit.phone_number = newValue;
            }
          "
          :error="true"
          type="tel"
        />
        <input-text-area
          class="lg:col-span-full"
          name="address"
          libelle="Adresse postale"
          :value="beneficiaryToEdit.address"
          @input="
            (newValue) => {
              beneficiaryToEdit.address = newValue;
            }
          "
        />
        <!-- BEGIN Modify password form -->
        <password-modifier
          grid="grid grid-cols-12 gap-x-4 gap-y-5"
          password-input-size="col-span-full lg:col-span-6"
          confirm-password-input-size="lg:col-span-6"
          auth-password-input-size="lg:col-span-6"
          confirm-button-size="lg:col-span-6"
          :password="beneficiaryToEdit.password"
          :confirm-password="beneficiaryToEdit.password_confirmation"
          :auth-password="authPassword"
          @fillPassword="
            (newValue) => {
              beneficiaryToEdit.password = newValue;
            }
          "
          @fillConfirmPassword="
            (newValue) => {
              beneficiaryToEdit.password_confirmation = newValue;
            }
          "
          @fillAuthPassword="
            (newValue) => {
              authPassword = newValue;
            }
          "
          @confirmNewPassword="confirmNewPassword"
          :errors="errors"
        />
        <!-- END Modify password form -->
      </div>
      <div class="flex flex-col items-center mt-15 space-y-6">
        <action-button
          border-color="border-transparent"
          text-color="text-gray-900"
          bg-color="bg-indigo-300"
          bg-color-square="bg-indigo-200"
          icon-color="text-indigo-600"
          libelle="Détacher le carnet de parcours de la structure"
          :responsive="true"
          icon="log-out"
          @clickAction="changeDisplayDetachBeneficiaryPopup"
        />
        <p
          v-if="isDetachBeneficiaryDisplayed"
          class="text-indigo-600 italic text-sm"
        >
          Êtes vous sûr de vouloir détacher le Neopass de
          {{ beneficiaryToEdit.firstname }}
          {{ beneficiaryToEdit.lastname }} de votre structure ?
        </p>
        <div
          v-if="isDetachBeneficiaryDisplayed"
          class="flex space-x-5"
          id="detachButtons"
        >
          <action-button
            class="relative flex flex-row"
            border-color="border-pink-600"
            text-color="text-pink-600"
            bg-color="bg-pink-300"
            libelle="ANNULER"
            @clickAction="changeDisplayDetachBeneficiaryPopup"
          />
          <action-button
            class="relative flex flex-row"
            border-color="border-transparent"
            text-color="text-white"
            bg-color="bg-pink-600"
            libelle="CONFIRMER"
            type-shadow="shadow-pink"
            @clickAction="confirmDetachBeneficiary()"
          />
        </div>
      </div>
    </div>
  </popup>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';

import InputText from './../../../components/Inputs/InputText';
import InputSelect from './../../../components/Inputs/InputSelect';
import InputTextArea from './../../../components/Inputs/InputTextArea';
import ActionButton from '../../../components/ActionButton';
import PasswordModifier from '../../../components/Inputs/PasswordModifier.vue';

import SaveButton from './../../../components/SaveButton.vue';
import Popup from './../../../components/Popup';
import MessageHeader from './../../../components/MessageHeader';
import Icon from './../../../components/Icon';
import ProfilePicture from './../../../components/ProfilePicture.vue';

export default {
  name: 'EditBenficiaryPopup',

  props: {
    dataToShow: Boolean,
  },

  data() {
    return {
      authPassword: '',
      isDetachBeneficiaryDisplayed: false,
    };
  },

  components: {
    ProfilePicture,
    Popup,
    MessageHeader,
    Icon,
    InputText,
    InputSelect,
    InputTextArea,
    ActionButton,
    SaveButton,
    PasswordModifier,
  },

  computed: {
    ...mapGetters({
      saving: 'permissions/saving',
      messageHeader: 'permissions/messageHeader',
      beneficiaryToEdit: 'beneficiaries/beneficiaryToEdit',
      errors: 'beneficiaries/errors',
      structure: 'auth/structure',
    }),
  },

  methods: {
    ...mapActions({
      saveBeneficiary: 'beneficiaries/saveBeneficiary',
      saveNewPassword: 'beneficiaries/saveNewPassword',
      detachBeneficiary: 'beneficiaries/detachBeneficiary',
    }),

    ...mapMutations({
      SET_ERRORS: 'beneficiaries/SET_ERRORS',
      SET_SAVING: 'permissions/SET_SAVING',
      SET_BENEFICIARYTOEDIT: 'beneficiaries/SET_BENEFICIARYTOEDIT',
    }),

    changeToShow(val) {
      this.$emit('showChange', val);

      this.isAuthenticateAsk = false;
      this.isDetachBeneficiaryDisplayed = false;

      if (val) {
        this.beneficiaryToEdit.password_confirmation = '';
      }

      this.SET_ERRORS(null);
      this.SET_SAVING(false);
      this.authPassword = '';
    },

    confirmNewPassword() {
      this.saveNewPassword(this.authPassword).then(() => {
        if (!this.errors) {
          this.authPassword = '';
          this.beneficiaryToEdit.password = '';
          this.beneficiaryToEdit.password_confirmation = '';
        }
      });

    },

    changeDisplayDetachBeneficiaryPopup() {
      this.isDetachBeneficiaryDisplayed = !this.isDetachBeneficiaryDisplayed;
    },

    async confirmDetachBeneficiary() {
      try {
        this.detachBeneficiary();
        this.changeToShow(false);
        setTimeout(() => {
          this.SET_BENEFICIARYTOEDIT({});
        }, 200);
      } catch (error) {}
    },

    changeProfileImageToAnotherUser(urlPicture) {
      this.beneficiaryToEdit.profile_image = urlPicture;
      this.saveBeneficiary();
    },
  },
};
</script>
